import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Button from "../components/common/Button";
import useGET from "../services/useGET";
import Cookies from "js-cookie";
import { isPointInPolygon, getUserLocation } from "../utils/geolocation";
import useAuth from "../hooks/useAuth";

// Helper function to check if user is logged in
const isUserLoggedIn = () => {
    try {
        const token = localStorage.getItem('token');
        return !!token;
    } catch (error) {
        return false;
    }
};


/**
 * React component allows user to mark the different species that they have found in the area.
 * @returns {JSX.Element}
 */
export default function QuizPage() {
    const { data: areas } = useGET(`${process.env.REACT_APP_API_URL}/api/areas`);
    const [areaSpecies, setAreaSpecies] = useState([]);
    const [areaName, setArea] = useState("");
    const [selectedAreaId, setSelectedAreaId] = useState(null);
    const [selectedSpecies, setSelectedSpecies] = useState({});
    const [totalSelectedCheckboxes, setTotalSelectedCheckboxes] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [userMarkedSpecies, setUserMarkedSpecies] = useState([]);
    const [isLoadingUserSpecies, setIsLoadingUserSpecies] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [isInsideArea, setIsInsideArea] = useState(false);
    const [areaPins, setAreaPins] = useState([]);
    const [locationError, setLocationError] = useState(null);
    const [isCheckingLocation, setIsCheckingLocation] = useState(false);
    const userId = localStorage.getItem('userId');

    const [numberOfRequiredSpecies, setNumberOfRequiredSpecies] = useState(0)

    //TODO might be unessecary for this page
    const [totalNumberOfQuestions, setNumberOfQuestions] = useState(0)
    const [correct_required, setCorrect_Required] = useState(0)
    const [time_limit_minutes, setTime_Limit_Minutes] = useState(0)

    const { user, isAdmin, loading } = useAuth();
    
    const { data: requiredSpeciesData } = useGET(
        `${process.env.REACT_APP_API_URL}/api/quiz-settings/species_required`
    );

    useEffect(() => {
        if (requiredSpeciesData) {
            setNumberOfRequiredSpecies(requiredSpeciesData.numberOfRequiredSpecies);
        }
    }, [requiredSpeciesData]);

    // Fetch user's globally marked species from backend
    useEffect(() => {
        const fetchUserMarkedSpecies = async () => {
            if (!userId) return;
            
            setIsLoadingUserSpecies(true);
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/species`, {
                    method: "GET",
                    headers:{
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setUserMarkedSpecies(data.map(s => s.id));
                } else if (response.status === 404) {
                    // User has no marked species yet, that's okay
                    setUserMarkedSpecies([]);
                }
            } catch (error) {
                console.error('Error fetching user marked species:', error);
            } finally {
                setIsLoadingUserSpecies(false);
            }
        };
        
        fetchUserMarkedSpecies();
    }, [userId]);

    // Handle scroll to show/hide scroll to top button
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 200);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSelectedArea = async (event) => {
        const { value } = event.target;
        const selectedArea = areas?.find(area => area.name === value);
        
        setArea(value);
        setSelectedAreaId(selectedArea?.id);
        setIsInsideArea(false);
        setAreaPins([]);
        setLocationError(null);

        if (selectedArea) {
            try {
                // Fetch species for the area
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${selectedArea.id}`);
                const speciesData = await response.json();
                setAreaSpecies(speciesData || []);
                
                // Pre-check species that user has marked AND exist in this area
                const preCheckedSpecies = {};
                let count = 0;
                speciesData.forEach(species => {
                    if (userMarkedSpecies.includes(species.id)) {
                        preCheckedSpecies[species.id] = true;
                        count++;
                    }
                });
                setSelectedSpecies(preCheckedSpecies);
                setTotalSelectedCheckboxes(count);

                // Fetch area pins to check if user is inside
                setIsCheckingLocation(true);
                const pinsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/pins/${selectedArea.id}`);
                if (pinsResponse.ok) {
                    const pins = await pinsResponse.json();
                    setAreaPins(pins || []);
                    
                    // Check if user is inside the area
                    if (isAdmin){ 
                        setIsInsideArea(true)
                    }
                    else if (pins && pins.length >= 3) {
                        try {
                            const location = await getUserLocation();
                            setUserLocation(location);
                            const inside = isPointInPolygon(location, pins);
                            setIsInsideArea(inside);
                            
                            if (!inside) {
                                setLocationError("Du är inte inom området för detta quiz. Du måste befinna dig inom området för att kunna göra quizet.");
                            }
                        } catch (error) {
                            console.error("Error getting user location:", error);
                            setLocationError("Kunde inte hämta din position. Kontrollera att du har aktiverat platstjänster i din webbläsare.");
                        }
                    }
                }
                setIsCheckingLocation(false);
            } catch (error) {
                console.error("Error fetching data for area:", error);
                setAreaSpecies([]);
                setSelectedSpecies({});
                setTotalSelectedCheckboxes(0);
                setIsCheckingLocation(false);
            }
        } else {
            setAreaSpecies([]);
            setSelectedSpecies({});
            setTotalSelectedCheckboxes(0);
        }
    };

    const handleCheck = async (id, checked) => {
        // Always update UI immediately
        setSelectedSpecies((prevState) => ({
            ...prevState,
            [`${id}`]: checked,
        }));
        const totalChecked = Object.values({ ...selectedSpecies, [`${id}`]: checked }).filter(Boolean).length;
        setTotalSelectedCheckboxes(totalChecked);

        // Save to backend if user is logged in
        if (!userId) {
            console.warn('User not logged in - changes will not be saved');
            return;
        }

        try {
            if (checked) {
                // Mark species as seen
                const token = localStorage.getItem("token");
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/species/${id}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    }
                });
                
                if (response.ok) {
                    setUserMarkedSpecies(prev => [...prev, id]);
                }
            } else {
                // Unmark species
                const token = localStorage.getItem("token");
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/species/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    }
                });
                
                if (response.ok) {
                    setUserMarkedSpecies(prev => prev.filter(speciesId => speciesId !== id));
                }
            }
        } catch (error) {
            console.error('Error saving species selection:', error);
        }
    };

    const filteredSpecies = areaSpecies.filter(species =>
        species.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Group species by category
    const groupedSpecies = filteredSpecies.reduce((acc, species) => {
        const category = species.category_name || "Övrigt";
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(species);
        return acc;
    }, {});

    return (
        <div className="flex flex-col min-h-screen bg-offwhite font-body">
            <Navbar />

            <main className="relative w-full" style={{ height: 'calc(100vh - 56px)', marginTop: '56px' }}>
                <div className="container mx-auto px-4 py-8 h-full overflow-y-auto">
                    {/* How it works section */}
                    <div className="bg-sage border border-sage rounded-lg p-6 mb-8">
                        <h2 className="font-heading text-xl font-bold text-charcoal mb-4 text-center">
                            Så här fungerar quizet:
                        </h2>
                        <div className="space-y-4 text-charcoal max-w-3xl mx-auto text-center">
                            <div>
                                <p className="pt-1">
                                    <strong>Upptäck arter i naturen</strong> – Du måste befinna dig på plats i området för att 
                                    markera arter som du upptäckt
                                </p>
                            </div>
                            <div>
                                <p className="pt-1">
                                    <strong>Viktigt att tänka på</strong> – För att kunna göra quizet behöver du också faktiskt vara på plats i det angivna området för att ens kunna starta quizet
                                </p>
                            </div>
                            <div>
                                <p className="pt-1">
                                    <strong>Gör quizet</strong> – Du behöver ha hittat och markerat minst {numberOfRequiredSpecies}st arter i området för att låsa upp och göra quizet
                                </p>
                            </div>
                            <div>
                                <p className="pt-1">
                                    <strong>Naturborgarmärket</strong> – När du väl klarat quizet blir du berättigad att beställa ditt Naturborgarmärke!
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Vänsterkolumn - Kontrollpanel */}
                        <div className="w-full lg:w-80 flex-shrink-0">
                            <div className="bg-white rounded-lg shadow-lg p-6 border border-sage sticky top-20">
                                <h2 className="font-heading text-2xl font-bold text-center text-forest mb-6">
                                    Markera arter
                                </h2>

                                {/* Område select */}
                                <div className="mb-6">
                                    <label htmlFor="area-select" className="block text-charcoal font-semibold mb-2">
                                        Välj område
                                    </label>
                                    <select
                                        id="area-select"
                                        value={areaName}
                                        onChange={handleSelectedArea}
                                        className="w-full px-4 py-2 border border-sage rounded-lg focus:outline-none focus:ring-2 focus:ring-forest bg-white text-charcoal"
                                    >
                                        <option value="">Välj ett område</option>
                                        {areas?.map((area) => (
                                            <option key={area.id} value={area.name}>
                                                {area.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Sök art */}
                                <div className="mb-6">
                                    <label htmlFor="search-species" className="block text-charcoal font-semibold mb-2">
                                        Sök art
                                    </label>
                                    <input
                                        id="search-species"
                                        type="search"
                                        placeholder="Skriv artnamn..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-4 py-2 border border-sage rounded-lg focus:outline-none focus:ring-2 focus:ring-forest"
                                    />
                                </div>

                                {/* Progress */}
                                <div className="mb-6 text-center">
                                    <p className="font-heading text-xl font-bold text-forest">
                                        {totalSelectedCheckboxes}/{numberOfRequiredSpecies}
                                    </p>
                                    <p className="text-charcoal text-sm">arter valda</p>
                                    <div className="w-full bg-sage rounded-full h-2 mt-2">
                                        <div
                                            className="bg-forest h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${(totalSelectedCheckboxes / numberOfRequiredSpecies) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Gå vidare button */}
                                <Link
                                    to={{ pathname: "/take-quiz", state: { speciesOut: selectedSpecies, areaName: areaName } }}
                                    className={`block w-full ${(totalSelectedCheckboxes < numberOfRequiredSpecies || !isInsideArea) ? 'pointer-events-none opacity-50' : ''}`}
                                >
                                    <Button
                                        variant="default"
                                        className="w-full"
                                        disabled={totalSelectedCheckboxes < numberOfRequiredSpecies || !isInsideArea}
                                    >
                                        Gå vidare
                                    </Button>
                                </Link>

                                {/* Location status message */}
                                {isCheckingLocation && (
                                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-300">
                                        <p className="text-charcoal text-sm">
                                            <span className="font-bold">Kontrollerar position...</span>
                                        </p>
                                    </div>
                                )}

                                {locationError && (
                                    <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-300">
                                        <p className="text-charcoal text-sm">
                                            <span className="font-bold">{locationError}</span>
                                        </p>
                                    </div>
                                )}

                                {!isCheckingLocation && isInsideArea && areaName && (
                                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-300">
                                        <p className="text-charcoal text-sm">
                                            <span className="font-bold">Du är inom området!</span> Du kan göra quizet när du har hittat minst {numberOfRequiredSpecies} arter.
                                        </p>
                                    </div>
                                )}

                                {/* Observera text */}
                                <div className="mt-6 p-4 bg-beige rounded-lg border border-sage">
                                    <p className="text-charcoal text-sm">
                                        <span className="font-bold">Observera!</span> Du måste befinna dig på plats i aktuellt naturområde för att få utföra quizet.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Högerkolumn - Artlista */}
                        <div className="flex-grow">
                            {!areaName ? (
                                <div className="text-center py-16">
                                    <div className="inline-block p-8 bg-white rounded-lg shadow-lg border border-sage">
                                        <svg className="w-16 h-16 mx-auto mb-4 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <h3 className="font-heading text-2xl font-bold text-forest mb-2">
                                            Välj ett område för att komma igång
                                        </h3>
                                        <p className="text-charcoal">
                                            Använd menyn till vänster för att välja ett naturområde
                                        </p>
                                    </div>
                                </div>
                            ) : filteredSpecies.length === 0 ? (
                                <div className="text-center py-16">
                                    <div className="inline-block p-8 bg-white rounded-lg shadow-lg border border-sage">
                                        <p className="text-charcoal text-lg">Inga arter hittades</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    {Object.keys(groupedSpecies).sort().map((category, index) => (
                                        <div key={category} className={index > 0 ? "mt-12" : ""}>
                                            <h3 className="font-heading text-2xl font-bold text-forest mb-4 pb-2 border-b-2 border-sage">
                                                {category}
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {groupedSpecies[category].map((species) => (
                                                    <div
                                                        key={species.id}
                                                        className="bg-white rounded-lg shadow-md p-4 border border-sage hover:shadow-lg transition-shadow duration-200 cursor-pointer"
                                                        onClick={() => handleCheck(species.id, !selectedSpecies[species.id])}
                                                    >
                                                        <label className="flex items-center pointer-events-none">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!selectedSpecies[species.id]}
                                                                onChange={(event) => handleCheck(species.id, event.target.checked)}
                                                                className="w-5 h-5 text-forest bg-white border-sage rounded focus:ring-forest focus:ring-2"
                                                            />
                                                            <span className="ml-3 text-charcoal font-semibold">
                                                                {species.name}
                                                            </span>
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {/* Scroll to Top Button - Mobile Only */}
            {showScrollTop && (
                <button
                    onClick={scrollToTop}
                    className="fixed bottom-8 right-8 bg-forest text-white p-4 rounded-full shadow-lg hover:bg-leaf transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 z-50 lg:hidden"
                    aria-label="Scrolla till toppen"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                </button>
            )}

            <Footer />
        </div>
    );
}
